import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {Button, Space} from 'antd';
import {useDispatch, useSelector} from 'react-redux';
import {
  StyledKYCModal,
  StyledKYCModalCompanyName,
  StyledKYCModalMenu,
  StyledKYCModalContent,
  StyledKYCModalContentStepTitle,
  StyledKYCModalFooter
} from './StyledKYCModal';
import KYCModalScreen from '../components/KYCModalScreen';
import MenuSteps from '../components/KYCModal/MenuSteps';
import CompanyCreation from '../components/KYCModal/steps/CompanyCreation';
import LegalPerson from '../components/KYCModal/steps/LegalPerson';
import LegalRepresentative from '../components/KYCModal/steps/LegalRepresentative';
import ActivateAccount from '../components/KYCModal/steps/ActivateAccount';
import {alertActions} from '../../../state/actions/alert.actions';
import {bankingActions} from '../../../state/actions/banking.actions';
import {mainActions} from '../../../state/actions/main.actions';
import {transactionActions} from '../../../state/actions/transaction.actions';
import {KYCCompanyContext, useBankingCompanyState} from '../../../hooks';
import {
  KYCModalModeTypes,
  KYCCompanyStageConstants,
  bankingUserTypesConstants,
  localStorageKeysConstants
} from '../../../constants';
import {COMPANY_HEADER} from '../../../state/services/companyHeader';
import {objectHelpers} from '../../../utils/helpers';

const {REVIEW} = KYCCompanyStageConstants;
const {COMPANY, KYC} = KYCModalModeTypes;
const {COMPANY_ID} = localStorageKeysConstants;

const activateAccountStepKey = 'activate-account';

// The stages that reach the activation step with a wallet already behind them. `ubo_creating` also
// reports ULTIMATE_BENEFICIARY and has no wallet yet, so this decides only whether it is worth
// asking for the wire details — the step itself keys its copy on whether they arrived.
const walletStages = [
  KYCCompanyStageConstants.ULTIMATE_BENEFICIARY,
  KYCCompanyStageConstants.EMPLOYEE,
  KYCCompanyStageConstants.ACCOUNT,
  KYCCompanyStageConstants.REVIEW,
  KYCCompanyStageConstants.CARD
];

const getMenuSteps = ({t, enabledKyc, errorsKeys = [], pendingKeys = []}) => {
  const stepMenuTitle = (key) => t(`menu.${key}.title`);

  const stepMenuDescription = (key, number = '') => t(`menu.${key}.description${number}`);

  let steps = [
    {
      key: 'company',
      title: stepMenuTitle('company'),
      description: enabledKyc && <>{stepMenuDescription('company', 1)}<br/>{stepMenuDescription('company', 2)}</>
    }
  ];
  if (enabledKyc) {
    steps = [
      ...steps,
      {
        key: 'lr',
        title: stepMenuTitle('legalRepresentative'),
        description: stepMenuDescription('legalRepresentative')
      },
      {
        key: activateAccountStepKey,
        title: stepMenuTitle('activateAccount')
      },
      // {
      //   key: 'sso-connection',
      //   title: stepMenuTitle('ssoConnection')
      // },
      // {
      //   key: 'subscriptions',
      //   title: stepMenuTitle('subscriptions')
      // },
    ];
  }
  steps = steps.map(step => ({...step, error: errorsKeys.includes(step.key)}));
  steps = steps.map(step => ({...step, pending: pendingKeys.includes(step.key)}));
  return steps;
}

const checkReviewError = (companyState) => {
  let error = false;
  if (companyState && !objectHelpers.isEmptyObject(companyState)) {
    error = companyState.stage === REVIEW && companyState.state === 'review_has_errors';
  }
  return error;
}

const checkReviewPending = (companyState) => {
  let pending = false;
  if (companyState && !objectHelpers.isEmptyObject(companyState)) {
    pending = companyState.stage === REVIEW && companyState.state === 'review_pending';
  }
  return pending;
}


const KYCModal = ({
  onCancel,
  ...rest
}) => {
  const dispatch = useDispatch();
  const companyState = useBankingCompanyState();
  const {closeIcon = true, mode, open = false} = useSelector(state => state.main.kycWindowProps);
  const selectedCompany = useSelector(state => state.company);
  const companies = useSelector(state => state.user.companies);
  const [trans] = useTranslation(['main', 'quickStart', 'subscriptions']);
  const [menuStep, setMenuStep] = useState(1);
  const [step, setStep] = useState(1);
  const [enabledKyc, setEnabledKyc] = useState(false);
  const [walletLoading, setWalletLoading] = useState(false);
  // The company this window is for, fixed when it opens (or when step 1 creates a new one). Every
  // banking call below sends it explicitly instead of reading the selection at send time: the
  // selection can move under an open form — the boot fallback in getCompanies, the 403 fallback in
  // getEmployeeProfile, another tab — and a legal person must not be created on whichever company
  // that leaves selected.
  const [pinnedCompany, setPinnedCompany] = useState(null);
  // localStorage changes from another tab arrive as `storage` events and change nothing React can
  // see, so this only exists to re-render; the stored id itself is read fresh below.
  const [, setStorageChange] = useState(0);

  useEffect(() => {
    if (!open) {
      if (pinnedCompany) setPinnedCompany(null);
      return;
    }
    if (!pinnedCompany && selectedCompany.id) {
      setPinnedCompany({id: selectedCompany.id, name: selectedCompany.name});
    }
  }, [open, selectedCompany.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onStorage = (e) => e.key === COMPANY_ID && setStorageChange(n => n + 1);
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [open]);

  const kycHeaders = useMemo(
    () => pinnedCompany ? {[COMPANY_HEADER]: pinnedCompany.id} : undefined,
    [pinnedCompany]
  );

  // Step 1 is company creation: it registers a new company and selects it on purpose, then re-pins
  // to it in handleCompanyCreationFinish. Only from step 2 on is a different selection a problem.
  const storedCompanyId = localStorage.getItem(COMPANY_ID);
  const switchedCompanyId = useMemo(() => {
    if (!open || !pinnedCompany || step === 1) return null;
    if (selectedCompany.id && selectedCompany.id !== pinnedCompany.id) return selectedCompany.id;
    if (storedCompanyId && storedCompanyId !== pinnedCompany.id) return storedCompanyId;
    return null;
  }, [open, pinnedCompany, step, selectedCompany.id, storedCompanyId]);
  // Names can be missing: SET_COMPANY right after a company is created carries the id only.
  const getCompanyName = (id, knownName) =>
    knownName || companies.find(c => c.company_id === id)?.name || id;
  const pinnedCompanyName = pinnedCompany && getCompanyName(pinnedCompany.id, pinnedCompany.name);
  const switchedCompanyName = switchedCompanyId && getCompanyName(
    switchedCompanyId,
    switchedCompanyId === selectedCompany.id ? selectedCompany.name : null
  );

  const kycError = useMemo(() => checkReviewError(companyState), [companyState]);
  const kycPending = useMemo(() => checkReviewPending(companyState), [companyState]);

  const updateCurrentStep = ({steps, value, updateMenuStep = false, fallback = 1}) => {
    const currentStep = steps[value] || fallback;
    const currentEnabledKyc = currentStep > 1;
    let currentMenuStep = 1;

    if (currentStep !== step) setStep(currentStep);
    if (currentEnabledKyc !== enabledKyc) setEnabledKyc(currentEnabledKyc);

    if (updateMenuStep && currentStep > 1) currentMenuStep = currentStep - 1;
    if (currentMenuStep !== menuStep) setMenuStep(currentMenuStep);
  }

  useEffect(() => {
    let steps;
    if (companyState && !objectHelpers.isEmptyObject(companyState)) {
      const {stage} = companyState;
      // Every stage in CompanyStageEnum, not just the four this wizard has a form for. The banking
      // service derives the stage from the state prefix, and completing the legal representative
      // moves a company to `ubo_creating` — so the UBO and employee stages are on the main path,
      // not exotic. They used to fall through the `|| 1` default onto step 1, which is
      // CompanyCreation: a company halfway through KYC was shown the form for registering a
      // different company (H1MVP-4716).
      //
      // Everything past the legal representative maps to 4. The wizard collects no UBO or employee
      // data — that happens through invitations elsewhere — so there is nothing left to ask these
      // companies for, and 4 is already where "forms done, waiting on the account" lands.
      steps = {
        [KYCCompanyStageConstants.LEGAL_PERSON]: 2,
        [KYCCompanyStageConstants.LEGAL_REPRESENTATIVE]: 3,
        [KYCCompanyStageConstants.ULTIMATE_BENEFICIARY]: 4,
        [KYCCompanyStageConstants.EMPLOYEE]: 4,
        [KYCCompanyStageConstants.ACCOUNT]: 4,
        [KYCCompanyStageConstants.REVIEW]: 4,
        [KYCCompanyStageConstants.CARD]: 4
      };
      // A stage added server-side later must not land on "create a company" either. 4 is the safe
      // unknown: it reports where the application has got to and asks for nothing.
      updateCurrentStep({steps, value: stage, updateMenuStep: true, fallback: 4});
      // Every stage from the beneficial owners onwards lands on the activation step, and all of
      // them except `ubo_creating` already have a wallet: app-banking creates it in
      // `lp_creating_wallet`, which the FSM enters on the way out of `ubo_creating`. Only
      // account_stage used to load it, so a company at review, employee or card stage saw the
      // activation screen with an empty IBAN and BIC — while `review_pending` was telling it to
      // wire us the funds. Load the details for all of them (H1MVP-4736).
      if (walletStages.includes(stage)) {
        // Creating one stays account_stage only. It is a write and the FSM owns when it happens,
        // so a company that legitimately has no wallet yet must not be given one as a side effect
        // of opening the wizard; it gets the "setting up" copy instead.
        const enabledWalletCreation = stage === KYCCompanyStageConstants.ACCOUNT;
        const finishWithoutWallet = () => {
          dispatch(transactionActions.storeWireDetails(undefined));
          setWalletLoading(false);
        };

        setWalletLoading(true);
        dispatch(bankingActions.getCardsUsers({
          query: {user_type: bankingUserTypesConstants.LEGAL_PERSON},
          successCallback: (users) => {
            const legalPersonUser = users[0];
            // `.id` used to be read before the length check, so a company with no legal-person
            // user threw here. Reachable from more stages now that they all load the wallet.
            if (!legalPersonUser) return finishWithoutWallet();
            dispatch(bankingActions.storeLegalPersonDetails(legalPersonUser));
            dispatch(bankingActions.getUserWalletsMinimal({
              headers: kycHeaders,
              id: legalPersonUser.id,
              successCallback: (wallets) => {
                if (wallets.length > 0) {
                  dispatch(transactionActions.storeWireDetails(wallets[0], true));
                  setWalletLoading(false);
                  return;
                }
                if (!enabledWalletCreation) return finishWithoutWallet();
                dispatch(bankingActions.createWallet({
                  data: {user_id: legalPersonUser.id},
                  successCallback: (wallet) => {
                    dispatch(transactionActions.storeWireDetails(wallet));
                    setWalletLoading(false);
                  },
                  errorCallback: () => {
                    alertActions.error(trans('subscriptions:errors.walletCreationError'));
                    finishWithoutWallet();
                  },
                  headers: kycHeaders
                }));
              },
              errorCallback: finishWithoutWallet
            }));
          },
          // Without this the request failing left the step spinning for ever.
          errorCallback: finishWithoutWallet,
          headers: kycHeaders
        }));
      }
    }
  }, [companyState]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mode !== KYC) {
      const steps = {
        [COMPANY]: 1,
        [KYC]: 2
      };
      updateCurrentStep({steps, value: mode});
    }
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open && mode === COMPANY) {
      enabledKyc && setEnabledKyc(false);
      step !== 1 && setStep(1);
      menuStep !== 1 && setMenuStep(1);
    }
  }, [mode, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const t = (key) => trans(`quickStart:kyc.${key}`);

  const handleCancel = (...rest) => {
    dispatch(mainActions.closeKycWindow());
    onCancel && onCancel(...rest);
  }

  const increaseStep = () => setStep(step + 1);

  const increaseMenuStep = () => setMenuStep(menuStep + 1);

  const menuSteps = useMemo(() => {
    const errorsKeys = [];
    const pendingKeys = [];
    if (kycError) errorsKeys.push(activateAccountStepKey);
    if (kycPending) pendingKeys.push(activateAccountStepKey);
    return getMenuSteps({t, enabledKyc, errorsKeys, pendingKeys});
  }, [enabledKyc, kycError, kycPending]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCompanyCreationFinish = ({enabledKyc, companyId, companyName}) => {
    // The company just created is now the selected one, and the one the rest of the wizard is for.
    if (companyId) setPinnedCompany({id: companyId, name: companyName});
    setEnabledKyc(enabledKyc);
    enabledKyc ? increaseStep() : handleCancel();
  }

  const finishStep = (trigger) => {
    increaseStep();
    increaseMenuStep();
    dispatch(bankingActions.setKYCCompanyState({trigger, headers: kycHeaders}));
  }

  const handleLegalPersonFinish = () => finishStep('set_lp_completed');

  const handleLegalRepresentativeFinish = () => {
    setWalletLoading(true);
    finishStep('initiate_lp_wallet_create');
  }

  const getStepContent = () => {
    const screens = {
      1: (
        <CompanyCreation
          onFinish={handleCompanyCreationFinish}
        />
      ),
      2: (
        <LegalPerson
          onFinish={handleLegalPersonFinish}
        />
      ),
      3: (
        <LegalRepresentative
          onFinish={handleLegalRepresentativeFinish}
        />
      ),
      4: (
        <ActivateAccount
          loading={walletLoading}
          warning={kycPending && t('errors.reviewPending')}
          error={kycError && t('errors.reviewError')}
          onFinish={handleCancel}
        />
      )
    }
    return screens[step] || null;
  }

  // The steps are unmounted rather than disabled, so no submit can fire from them at all.
  const stepContent = switchedCompanyId
    ? (
      <KYCModalScreen
        enabledCancel={false}
        okText={t('companyChanged.close')}
        onOk={handleCancel}
      >
        <Space direction='vertical' size={16} className='left-side-content'>
          <StyledKYCModalContentStepTitle>{t('companyChanged.title')}</StyledKYCModalContentStepTitle>
          <span>
            {t('companyChanged.description', {kycCompany: pinnedCompanyName, selectedCompany: switchedCompanyName})}
          </span>
        </Space>
      </KYCModalScreen>
    )
    : getStepContent();

  return (
    <StyledKYCModal
      closeIcon={closeIcon}
      onCancel={handleCancel}
      footer={null}
      open={open}
      {...rest}
    >
      <KYCCompanyContext.Provider value={pinnedCompany}>
        <StyledKYCModalMenu>
          <StyledKYCModalContent>
            {step > 1 && pinnedCompanyName && (
              <StyledKYCModalCompanyName>
                {t('companyLabel')}: <strong>{pinnedCompanyName}</strong>
              </StyledKYCModalCompanyName>
            )}
            <MenuSteps
              steps={menuSteps}
              activeNum={menuStep}
            />
          </StyledKYCModalContent>
          <StyledKYCModalFooter>
            <Button
              onClick={handleCancel}
              size='large'
            >
              {t('continueLater')}
            </Button>
          </StyledKYCModalFooter>
        </StyledKYCModalMenu>
        {stepContent}
      </KYCCompanyContext.Provider>
    </StyledKYCModal>
  );
}

KYCModal.propTypes = {
  onCancel: PropTypes.func
}

export default KYCModal;

